/**
 * POSTs a message and reads the SSE response with fetch + ReadableStream (EventSource cannot POST).
 * onEvent(name, data) is called per frame; comment lines (heartbeats) are ignored.
 * Resolves when the stream ends; rejects with { status, body } for a non-200 response.
 */
export async function streamMessage(conversationId, body, onEvent, signal) {
	const response = await fetch(`/api/assistant/conversations/${conversationId}/messages`, {
		method: "POST",
		headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
		credentials: "include",
		body: JSON.stringify(body),
		signal,
	});
	if (response.status !== 200) {
		let parsed = null;
		try {
			parsed = await response.json();
		} catch (e) {
			parsed = null;
		}
		throw { status: response.status, body: parsed };
	}
	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";
	for (;;) {
		const { value, done } = await reader.read();
		if (done) break;
		// Normalise the whole buffer, not the chunk, so a CR ending one chunk joins the LF starting the next.
		buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, "\n");
		buffer = dispatchFrames(buffer, onEvent);
	}
	// The stream ended: flush the decoder and deliver a last frame that had no trailing blank line, provided it is
	// complete (it has its data line; a frame cut off mid-line would fail to parse and is reported like any bad event).
	buffer = dispatchFrames((buffer + decoder.decode()).replace(/\r\n/g, "\n"), onEvent);
	if (/^data: /m.test(buffer)) dispatchFrame(buffer.replace(/\n+$/, ""), onEvent);
}

/** Dispatches every frame terminated by a blank line and returns the unterminated remainder. */
function dispatchFrames(buffer, onEvent) {
	let rest = buffer;
	let index;
	while ((index = rest.indexOf("\n\n")) >= 0) {
		dispatchFrame(rest.slice(0, index), onEvent);
		rest = rest.slice(index + 2);
	}
	return rest;
}

function dispatchFrame(frame, onEvent) {
	if (!frame || frame.startsWith(":")) return;
	let name = "message";
	let data = "";
	frame.split("\n").forEach((line) => {
		if (line.startsWith("event: ")) name = line.slice(7);
		else if (line.startsWith("data: ")) data += line.slice(6);
	});
	try {
		onEvent(name, data ? JSON.parse(data) : null);
	} catch (e) {
		console.error("Bad assistant event", name, e);
	}
}
